'use client'

import { createContext, useContext } from 'react'
import type { User } from '@/lib/types'

/**
 * ChatKit credentials interface
 */
export interface IChatKitCredentials {
  /**
   * Current user object (null if not authenticated)
   */
  user: User | null
  /**
   * User's API key for ChatKit authentication (null if not available)
   */
  userApiKey: string | null
  /**
   * Whether credentials are ready to use
   */
  isReady: boolean
}

const ChatKitCredentialsContext = createContext<IChatKitCredentials | null>(
  null,
)

interface IChatKitCredentialsProviderProps {
  /**
   * User object from server component
   */
  user: User | null
  /**
   * User's API key from server component
   */
  userApiKey: string | null
  /**
   * Child components
   */
  children: React.ReactNode
}

/**
 * Provider component that provides ChatKit credentials to entire application
 * Credentials are fetched server-side and passed down via props
 */
export function ChatKitCredentialsProvider({
  user,
  userApiKey,
  children,
}: IChatKitCredentialsProviderProps) {
  const credentials: IChatKitCredentials = {
    user,
    userApiKey,
    isReady: Boolean(user && userApiKey),
  }

  return (
    <ChatKitCredentialsContext.Provider value={credentials}>
      {children}
    </ChatKitCredentialsContext.Provider>
  )
}

/**
 * Hook to access ChatKit credentials from context
 * @returns ChatKit credentials (user, userApiKey, isReady)
 * @throws Error if used outside of ChatKitCredentialsProvider
 */
export function useChatKitCredentials(): IChatKitCredentials {
  const context = useContext(ChatKitCredentialsContext)
  if (!context) {
    throw new Error(
      'useChatKitCredentials must be used within ChatKitCredentialsProvider',
    )
  }
  return context
}
